import React from 'react';
import { Box, Typography, Link, Chip } from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { confidenceColor } from '../../../../utils/confidence';
import { formatDate, formatPriceRange, getConfidenceColor, safeNumber, type PriceSourceContext } from './format';

// One component per section of the cost-price tooltip. PriceSourceTooltip's
// SECTIONS table decides which of them a given price source shows.

type SectionProps = { ctx: PriceSourceContext };

/** Where the price came from (or the error), and the matched item. */
export const SourceSection: React.FC<SectionProps> = ({ ctx: { details, historicalData, isErrorCase } }) => (
  <Box sx={{
    p: 1,
    borderRadius: 1,
    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
    borderLeft: 3,
    borderColor: getConfidenceColor(details?.confidence)
  }}>
    <Typography variant="caption" sx={{
      color: 'text.secondary',
      display: 'block',
      mb: 0.5
    }}>
      {isErrorCase ? 'Error Information' : 'Price Source'}
    </Typography>
    <Typography variant="body2" sx={{
      fontWeight: 500,
      color: isErrorCase ? 'error.main' : 'text.primary'
    }}>
      {details?.source || 'Unknown source'}
    </Typography>
    {/* Show matched historical item name instead of current item name */}
    {historicalData?.selected_item_name ? (
      <Typography variant="body2" sx={{
        color: 'text.secondary',
        fontSize: '0.85rem',
        mt: 0.5
      }}>
        {historicalData.selected_item_name} - Historical Quote
      </Typography>
    ) : details?.title && (
      <Typography variant="body2" sx={{
        color: 'text.secondary',
        fontSize: '0.85rem',
        mt: 0.5
      }}>
        {details.title}
      </Typography>
    )}

    {/* Error Details Section */}
    {isErrorCase && details?.error_details && (
      <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
        <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
          Error Details:
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {details.error_details}
        </Typography>
      </Box>
    )}
  </Box>
);

/** The other historical records behind the quote, behind a toggle. */
const OtherRecords: React.FC<SectionProps> = ({ ctx: { historicalData, showHistoricalDetails, toggleHistoricalDetails } }) => {
  if (!historicalData) return null;
  return (
    <Box sx={{
      borderTop: 1,
      borderColor: 'divider',
      pt: 1,
      mt: 1
    }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem', mb: 0.5 }}>
        ℹ️ Selected from {historicalData.records_count} historical records
        {historicalData.price_range && historicalData.price_range.count > 1 &&
          ` (range: $${historicalData.price_range.min}-$${historicalData.price_range.max})`
        }
      </Typography>

      {/* Simple expandable list of other records */}
      {historicalData.individual_records && historicalData.individual_records.length > 1 && (
        <Box sx={{
          cursor: 'pointer',
          mt: 0.5
        }} onClick={toggleHistoricalDetails}>
          <Typography variant="caption" sx={{
            color: 'primary.main',
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            {showHistoricalDetails ? '▼' : '▶'} View other {historicalData.records_count - 1} records
          </Typography>

          {/* Simple list of other records */}
          {showHistoricalDetails && (
            <Box sx={{
              mt: 1,
              pl: 1,
              borderLeft: 2,
              borderColor: 'divider'
            }}>
              {historicalData.individual_records.slice(1).map((record) => (
                <Box key={record.id} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    display: 'block'
                  }}>
                    💰 ${record.price?.toFixed(2) || 'N/A'} • {record.customer_name || 'Unknown'} • {record.date ? formatDate(record.date) : 'No date'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/** The quote history the price came from (dimmed when market research won). */
export const QuoteHistorySection: React.FC<SectionProps> = ({ ctx }) => {
  const { historicalData, serpWasPreferred } = ctx;
  if (!historicalData) return null;
  return (
    <Box sx={{
      p: 1.5,
      borderRadius: 1,
      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      borderLeft: 3,
      borderColor: getConfidenceColor(historicalData.confidence),
      // Dim the box if SERP was preferred
      opacity: serpWasPreferred ? 0.6 : 1
    }}>
      {/* Warning Banner if SERP was preferred */}
      {serpWasPreferred && (
        <Box sx={{
          mb: 1.5,
          p: 1,
          borderRadius: 1,
          bgcolor: 'warning.light',
          color: 'warning.contrastText',
          border: 1,
          borderColor: 'warning.main'
        }}>
          <Typography variant="caption" sx={{
            fontWeight: 600,
            display: 'block',
            fontSize: '0.75rem'
          }}>
            ⚠️ Historical match quality too low - NOT USED
          </Typography>
          <Typography variant="caption" sx={{
            fontSize: '0.75rem',
            display: 'block',
            mt: 0.3
          }}>
            Market research used instead (see below)
          </Typography>
        </Box>
      )}

      {/* Clean Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HistoryIcon sx={{ fontSize: 16, color: getConfidenceColor(historicalData.confidence) }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: getConfidenceColor(historicalData.confidence) }}>
            Quote History {serpWasPreferred ? '(Reference Only)' : ''}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={historicalData.confidence?.toUpperCase() || 'UNKNOWN'}
          color={confidenceColor(historicalData.confidence)}
          sx={{ fontSize: '0.75rem', height: 22 }}
        />
      </Box>

      {/* Key Information Only */}
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.8, fontSize: '0.9rem' }}>
          💰 ${safeNumber(historicalData.suggested_price)}
        </Typography>

        {/* Show matched historical item name */}
        {historicalData.selected_item_name && (
          <Typography variant="body2" sx={{
            color: 'text.primary',
            fontSize: '0.85rem',
            mb: 0.8,
            fontStyle: 'italic',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            p: 0.5,
            borderRadius: 0.5
          }}>
            📦 Matched: "{historicalData.selected_item_name}"
          </Typography>
        )}

        {historicalData.selected_rfq_id && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 0.5 }}>
            📋 RFQ {historicalData.selected_rfq_id}
            {historicalData.selected_customer && ` • ${historicalData.selected_customer}`}
          </Typography>
        )}

        {historicalData.most_recent_date && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 0.5 }}>
            📅 {formatDate(historicalData.most_recent_date)}
          </Typography>
        )}

        {/* Show the actual quoted price that was used (not the AI-suggested original) */}
        {historicalData.suggested_price > 0 && (
          <Typography variant="body2" sx={{
            color: 'success.main',
            fontSize: '0.85rem',
            fontWeight: 500,
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
            p: 0.5,
            borderRadius: 0.5
          }}>
            ✅ Quoted Price Used: ${safeNumber(historicalData.suggested_price)}
          </Typography>
        )}
      </Box>

      {/* Optional: Show if multiple records were considered */}
      {historicalData.records_count > 1 && <OtherRecords ctx={ctx} />}
    </Box>
  );
};

/** The market price range the research found. */
export const PriceRangeSection: React.FC<SectionProps> = ({ ctx: { details } }) => (
  <Box>
    <Typography variant="caption" sx={{
      color: 'text.secondary',
      display: 'block',
      mb: 0.5
    }}>
      Current Market Price Range
    </Typography>
    <Typography variant="body2" sx={{
      color: 'text.secondary',
      fontSize: '0.85rem'
    }}>
      {formatPriceRange(details.price_range)}
    </Typography>
  </Box>
);

/** "Market Research (Price Used)", when it won over a weak history. */
export const MarketResearchBanner: React.FC<SectionProps> = () => (
  <Box sx={{
    p: 1.5,
    borderRadius: 1,
    bgcolor: 'success.light',
    color: 'success.contrastText',
    border: 2,
    borderColor: 'success.main'
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
        ✅ Market Research (Price Used)
      </Typography>
      <Chip
        size="small"
        label="ACTIVE"
        color="success"
        sx={{ fontSize: '0.75rem', height: 22, fontWeight: 600 }}
      />
    </Box>
    <Typography variant="caption" sx={{ fontSize: '0.75rem', display: 'block' }}>
      Historical match quality was too low - using current market pricing instead
    </Typography>
  </Box>
);

const linkSx = (color: string) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: '0.8rem',
  color,
  fontWeight: 500
});

/** Up to three links: the search page, the best price and an alternative. */
export const ShoppingLinks: React.FC<SectionProps> = ({ ctx: { details, serpWasPreferred } }) => {
  const searchUrl = details?.optimized_search_url || details?.analysis_metadata?.optimized_search_url;
  const secondary = details?.secondary_source && typeof details.secondary_source === 'object' && details.secondary_source.url
    ? (details.secondary_source as { price: number; retailer: string; title: string; url: string | null; confidence: string; position: number; })
    : null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* 1. Google Shopping Search Page (SERP API) */}
      {searchUrl && (
        <Box sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: serpWasPreferred ? 'primary.light' : 'info.light',
          color: serpWasPreferred ? 'primary.contrastText' : 'info.contrastText',
          border: serpWasPreferred ? 2 : 0,
          borderColor: serpWasPreferred ? 'primary.main' : 'transparent'
        }}>
          <Typography variant="caption" sx={{
            fontWeight: 600,
            display: 'block',
            mb: 0.5
          }}>
            🛍️ Google Shopping Search Page {serpWasPreferred ? '(Price Source)' : ''}
          </Typography>
            <Link
            href={searchUrl || ''}
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx('info.contrastText')}
            >
            Compare prices from all retailers
              <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
            </Link>
        </Box>
      )}

      {/* 2. Primary Product (Retailer Comparison or Product Page) */}
      {details?.primary_source?.url && (
        <Box sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: serpWasPreferred ? 'success.main' : 'success.light',
          color: 'success.contrastText',
          border: serpWasPreferred ? 2 : 0,
          borderColor: serpWasPreferred ? 'success.dark' : 'transparent'
        }}>
          <Typography variant="caption" sx={{
            fontWeight: serpWasPreferred ? 700 : 600,
            display: 'block',
            mb: 0.5
          }}>
            {serpWasPreferred ? '💰 PRICE USED: ' : '🥇 Best Price: '}${details.primary_source.price?.toFixed(2)} at {details.primary_source.retailer}
          </Typography>
            <Link
            href={details.primary_source.url || ''}
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx('success.contrastText')}
            >
            {details.primary_source.url.includes('comparisons') ?
              `View all retailers for this product` :
              `Buy now from ${details.primary_source.retailer}`}
              <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
            </Link>
        </Box>
      )}

      {/* 3. Secondary Product (Retailer Comparison or Product Page) */}
      {secondary && (
        <Box sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: 'warning.light',
          color: 'warning.contrastText'
        }}>
          <Typography variant="caption" sx={{
            fontWeight: 600,
            display: 'block',
            mb: 0.5
          }}>
              🥈 Alternative: ${secondary.price?.toFixed(2)} at {secondary.retailer}
          </Typography>
            <Link
              href={secondary.url || ''}
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx('warning.contrastText')}
            >
              {secondary.url && secondary.url.includes('comparisons') ?
                `View all retailers for this product` :
                `Buy now from ${secondary.retailer}`}
              <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5 }} />
            </Link>
        </Box>
      )}
    </Box>
  );
};

const matchChipSx = {
  bgcolor: 'success.main',
  color: 'success.contrastText',
  px: 0.5,
  borderRadius: 0.5,
  fontSize: '0.75rem'
};

/** The AI's reasoning, its match signals and how it picked the price. */
export const AiAnalysisSection: React.FC<SectionProps> = ({ ctx: { details } }) => {
  const analysis = details.groq_analysis;
  if (!analysis) return null;
  return (
    <Box sx={{
      p: 1,
      borderRadius: 1,
      bgcolor: 'info.light',
      color: 'info.contrastText'
    }}>
      <Typography variant="caption" sx={{
        fontWeight: 600,
        display: 'block',
        mb: 0.5
      }}>
        🤖 AI Analysis
      </Typography>
      <Typography variant="body2" sx={{
        fontSize: '0.8rem',
        mb: 1
      }}>
        {analysis.reasoning}
      </Typography>

      {/* Match Quality Indicators - with safe access */}
      {analysis.match_quality && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {analysis.match_quality.brand_match && (
            <Typography variant="caption" sx={matchChipSx}>
              ✓ Brand Match
            </Typography>
          )}
          {analysis.match_quality.model_match && (
            <Typography variant="caption" sx={matchChipSx}>
              ✓ Model Match
            </Typography>
          )}
          {analysis.is_exact_match && (
            <Typography variant="caption" sx={matchChipSx}>
              ✓ Exact Match
            </Typography>
          )}
        </Box>
      )}

      {/* Price Selection Info - with safe access */}
      {analysis.price_analysis && (
        <Typography variant="body2" sx={{
          fontSize: '0.75rem',
          fontStyle: 'italic'
        }}>
          {analysis.price_analysis.selected_for_lowest_price
            ? `🎯 Selected as lowest price among ${analysis.price_analysis.good_matches_found || 0} good matches`
            : `Selected from ${analysis.price_analysis.good_matches_found || 0} matches`
          }
        </Typography>
      )}
    </Box>
  );
};

/** The research's explanation, whether it is estimated, and its confidence. */
export const ExplanationSection: React.FC<SectionProps> = ({ ctx: { details } }) => (
  <Box sx={{
    p: 1,
    borderRadius: 1,
    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
  }}>
    <Typography variant="body2" sx={{
      color: 'text.secondary',
      fontSize: '0.85rem'
    }}>
      {details?.explanation || 'No explanation available'}
    </Typography>
    {details?.is_estimated && (
      <Typography variant="body2" sx={{
        color: 'warning.main',
        fontSize: '0.85rem',
        mt: 0.5,
        fontStyle: 'italic'
      }}>
        This is an estimated price
      </Typography>
    )}
    <Typography variant="body2" sx={{
      color: getConfidenceColor(details?.confidence),
      fontSize: '0.85rem',
      mt: 0.5,
      fontWeight: 500
    }}>
      Confidence: {details?.confidence || 'Unknown'}
    </Typography>
  </Box>
);
